import { datadogRum, type PropagatorType } from '@datadog/browser-rum';
import { angularPlugin } from '@datadog/browser-rum-angular';
import { PathNormalizer, scrubRumEvent } from './scrub-rum-event';

/** Matches `environment.env`; `local` is left out so dev machines don't report. */
export const RUM_ENABLED_ENVS = ['development', 'QA', 'production'];

const TRACE_PROPAGATORS: PropagatorType[] = ['datadog', 'tracecontext'];

/**
 * The router integration turns on manual views, and the first route view starts only after the app
 * initialises. Without a view RUM drops events, including the `GET /api/user` that runs during setup.
 */
export const STARTUP_VIEW_NAME = 'app-startup';

export interface StartRumOptions {
  applicationId: string;
  clientToken: string;
  service: string;
  env: string;
  version: string;
  /** Percent of sessions to track. */
  sessionSampleRate?: number;
  /** Request URL prefixes that get trace headers, so RUM requests link to their APM traces. */
  tracingUrls?: string[];
  /** Applied to view and resource paths before sending, e.g. spotlight-ui's `stripLocationPrefix`. */
  normalizePath?: PathNormalizer;
}

/**
 * Starts Datadog RUM. Called from `main.ts` before Angular bootstraps. No-op until the
 * application ID and client token are configured, and on `local`.
 */
export function startRum({
  applicationId, clientToken, service, env, version, sessionSampleRate = 100, tracingUrls = [], normalizePath
}: StartRumOptions): void {
  if (!applicationId || !clientToken || !RUM_ENABLED_ENVS.includes(env) || datadogRum.getInitConfiguration()) {
    return;
  }

  datadogRum.init({
    applicationId,
    clientToken,
    site: 'datadoghq.com',
    service,
    env,
    version: version || undefined,
    sessionSampleRate,
    // Replay is a separate decision (customer PII).
    sessionReplaySampleRate: 0,
    defaultPrivacyLevel: 'mask',
    trackUserInteractions: true,
    enablePrivacyForActionName: true,
    trackResources: true,
    trackLongTasks: true,
    // W3C `traceparent` plus the `x-datadog-*` headers. The API gateways' CORS must allow them.
    allowedTracingUrls: tracingUrls.map(match => ({ match, propagatorTypes: TRACE_PROPAGATORS })),
    plugins: [angularPlugin({ router: true })],
    beforeSend: event => scrubRumEvent(event, normalizePath)
  });
  datadogRum.startView(STARTUP_VIEW_NAME);
  // Same values as Amplitude's `UI Version`, so Legacy and Next Gen can be compared on one service.
  datadogRum.setGlobalContextProperty('uiVersion', 'Legacy');
}
